import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Space,
  Stack,
  Table,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useModals } from '@mantine/modals';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty, debounce } from 'lodash';
import { Settings, X, Trash } from 'tabler-icons-react';
import produce from 'immer';
import { showNotification } from '@mantine/notifications';
import { objectsDefListSelector } from '../../reducers/objectReducers/objectSelector';
import { heightScreen } from '../../utils/windowSize';
import SheetSwitcher from './SheetSwitcher';

// -------------------------------------------------
// Config: exclude system-like target fields
// -------------------------------------------------
const EXCLUDED_TARGET_FIELDS = [
  'systemversion',
  'issystem',
  'systemcategory',
  'isdemo',
  'crumbtrail',
  'crumbtrailIds',
  'creationdate',
  'objId',
  'perspectives',
  'uuid',
  'state',
  'createdAt',
  'updatedAt',
  'owner',
  'istemplate',
  'riskpriority',
  'clause',
];

// -------------------------------------------------
// Small helpers
// -------------------------------------------------
const toText = (v) =>
  v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);

const SafeCell = memo(({ label, value, maxWidth = 260 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <Text
      size="xs"
      weight={600}
      title={label}
      style={{
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Text>
    {value ? (
      value.length <= 120 ? (
        <Text
          size="xs"
          style={{
            maxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Text>
      ) : (
        <Tooltip
          withArrow
          label={value}
          position="bottom"
          styles={{ body: { maxWidth: 420, wordBreak: 'break-word' } }}
        >
          <Text
            size="xs"
            style={{
              maxWidth,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </Text>
        </Tooltip>
      )
    ) : (
      <Text size="xs" color="dimmed">
        —
      </Text>
    )}
  </div>
));
SafeCell.displayName = 'SafeCell';

// 1->N mapping helpers
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const includesTarget = (val, target) => asArray(val).includes(target);

const addTargetForIndex = (draft, index, targetAttr) => {
  const arr = asArray(draft[index]);
  if (!arr.includes(targetAttr)) draft[index] = [...arr, targetAttr];
};

const removeTargetForIndex = (draft, index, targetAttr) => {
  const arr = asArray(draft[index]).filter((t) => t !== targetAttr);
  draft[index] = arr.length === 0 ? null : arr.length === 1 ? arr[0] : arr;
};

// -------------------------------------------------
// Main component
// -------------------------------------------------
export default function FieldTab({
  responseUpFile,
  valueSelect,
  handleNextStepMappingData,
  prevStep,
  isLoading,
  opened = {},
  setOpened = () => {},
  checked = {},
  setChecked,
  columnObjectsDefListImport,
  getDataFromExcel,
  isLoadingLoadDataExcel,
  active,
  activePage,
  setPage,
  // --- props switcher
  sheets = [],
  activeSheet = 0,
  onSwitchSheet,
  indexMappings,
  setIndexMappings,
}) {
  const theme = useMantineTheme();
  const modals = useModals();
  const objDefs = useSelector(objectsDefListSelector);
  // const [indexMappings, setIndexMappings] = useState({});

  // Debounced overrides → backend
  const sendEnumOverrideDebounced = React.useMemo(
    () =>
      debounce((payload) => {
        if (!responseUpFile?.savedFileReference) return;
        getDataFromExcel(activePage, payload);
      }, 250),
    [getDataFromExcel, activePage, responseUpFile?.savedFileReference]
  );

  const sendColumnRemapDebounced = React.useMemo(
    () =>
      debounce((payload) => {
        if (!responseUpFile?.savedFileReference) return;
        getDataFromExcel(activePage, payload);
      }, 150),
    [getDataFromExcel, activePage, responseUpFile?.savedFileReference]
  );

  useEffect(() => {
    return () => {
      sendEnumOverrideDebounced.cancel?.();
      sendColumnRemapDebounced.cancel?.();
    };
  }, [sendEnumOverrideDebounced, sendColumnRemapDebounced]);
  // ---------- Object target fields
  const specificObj = objDefs?.find((o) => o?.key === valueSelect);

  const targetFields = useMemo(() => {
    const norm = (s) => (s ?? '').toString().trim();
    return (specificObj?.objFormData ?? [])
      .map((f) => {
        const attrName = norm(
          f?.attrName || f?.name || f?.key || f?.code || ''
        );
        const label = norm(f?.label || attrName);
        return attrName && label ? { value: attrName, label } : null;
      })
      .filter(Boolean)
      .filter((f) => !EXCLUDED_TARGET_FIELDS.includes(f.value));
  }, [specificObj?.objFormData]);

  const targetFieldsByAttr = useMemo(() => {
    const map = new Map();
    targetFields.forEach((t) => map.set(t.value, t.label));
    return map;
  }, [targetFields]);

  // ---------- Headers from server
  const [columnHeader, setColumnHeader] = useState([]); // [{ name, index }]
  const [columnRemove, setColumnRemove] = useState([]);

  useEffect(() => {
    setColumnHeader(responseUpFile?.headers ?? []);
  }, [responseUpFile?.headers]);

  const headerRowIndex = responseUpFile?.metadata?.headerRowIndex ?? 0;

  const mappedColumns = responseUpFile?.metadata?.mappedColumns ?? {};
  const mappingRules = responseUpFile?.metadata?.mappingRules ?? {};
  const fieldAnalysis = responseUpFile?.metadata?.fieldAnalysis ?? {};
  const [enumOverrides, setEnumOverrides] = useState({});

  // reset UI local quand la feuille change
  useEffect(() => {
    setIndexMappings({});
    // setColumnObjectsDefListImport({});
    setChecked({});
    setColumnRemove([]);
    setRecordIndex(0);
    setPage(1);
    setEnumOverrides({});
  }, [activeSheet, setPage, setChecked, setIndexMappings]);

  const visibleHeaders = useMemo(
    () =>
      (columnHeader ?? []).filter(
        (h) => h?.name && !columnRemove.includes(h.name)
      ),
    [columnHeader, columnRemove]
  );

  useEffect(() => {
    if (active !== 1) return;
    const mapped = responseUpFile?.metadata?.mappedColumns ?? {}; // targetField → index

    // ✅ INVERSER : convertir targetField → index vers index → targetField
    const indexToTarget = {};
    Object.entries(mapped).forEach(([targetField, indexStr]) => {
      if (indexStr != null) {
        // Gérer 1→N mapping : une colonne peut alimenter plusieurs targets
        if (indexToTarget[indexStr]) {
          // Convertir en array si pas déjà fait
          const existing = Array.isArray(indexToTarget[indexStr])
            ? indexToTarget[indexStr]
            : [indexToTarget[indexStr]];
          indexToTarget[indexStr] = [...existing, targetField];
        } else {
          indexToTarget[indexStr] = targetField;
        }
      }
    });

    setIndexMappings(indexToTarget);
  }, [responseUpFile?.metadata?.mappedColumns]);

  const handleSheetChangeWithConfirm = (nextId) => {
    if (isLoadingLoadDataExcel) return;
    const go = () => onSwitchSheet?.(Number(nextId));

    if (!hasLocalMappings) return go();

    return modals.openConfirmModal({
      title: 'Changer de feuille ?',
      children:
        'Vous avez des remappages/paramètres non appliqués. Changer de feuille va réinitialiser ces réglages pour la nouvelle feuille.',
      labels: { confirm: 'Changer de feuille', cancel: 'Annuler' },
      confirmProps: { color: 'red' },
      onConfirm: go,
    });
  };

  const hasLocalMappings = React.useMemo(() => {
    // remaps posés dans l’UI ?
    const hasRemaps =
      Object.values(indexMappings || {}).filter(Boolean).length > 0;
    // options uppercase/capitalize posées ?
    const hasOps = Object.keys(checked || {}).length > 0;
    // éventuels overrides enum locaux ?
    const hasEnum = Object.keys(enumOverrides || {}).length > 0;
    return hasRemaps || hasOps || hasEnum;
  }, [indexMappings, checked, enumOverrides]);

  // ---------- Record navigation inside current page
  const pageData = responseUpFile?.data ?? [];
  const [recordIndex, setRecordIndex] = useState(0);
  useEffect(() => setRecordIndex(0), [responseUpFile?.data, activePage]);

  // Chaque ligne: { input, output, _meta }
  const record = pageData?.[recordIndex] ?? {};
  const recordIn = record?.input ?? {};
  const recordOut = record?.output ?? {};

  const nextRecord = () =>
    setRecordIndex((i) => Math.min(i + 1, Math.max(pageData.length - 1, 0)));
  const prevRecord = () => setRecordIndex((i) => Math.max(i - 1, 0));

  // ---------- Selection for right panel (transforms)
  const [selectedTarget, setSelectedTarget] = useState(null);
  useEffect(() => setSelectedTarget(null), [valueSelect]);

  const getHeaderByTarget = useCallback(
    (targetAttr) => {
      const indexStr = Object.entries(indexMappings ?? {}).find(([idx, t]) =>
        includesTarget(t, targetAttr)
      )?.[0];

      if (!indexStr) return null;

      // Convertir index → headerName pour l'affichage
      return (
        responseUpFile?.headers?.find((h) => h.index === parseInt(indexStr))
          ?.name || null
      );
    },
    [indexMappings, responseUpFile?.headers]
  );

  const getIndexByTarget = useCallback(
    (targetAttr) =>
      Object.entries(indexMappings ?? {}).find(([idx, t]) =>
        includesTarget(t, targetAttr)
      )?.[0], // ✅ Retourne index
    [indexMappings]
  );

  const selectedHeaderForConfig = selectedTarget
    ? getHeaderByTarget(selectedTarget)
    : null;

  // ---------- DnD
  const DRAG_MIME = 'text/x-src-header';
  const onDragStartSource = (e, headerName) => {
    e.dataTransfer.setData(DRAG_MIME, headerName);
    e.dataTransfer.effectAllowed = 'copyMove';
  };
  const allowDrop = (e) => {
    if (e.dataTransfer.types?.includes(DRAG_MIME)) e.preventDefault();
  };

  const onDropToTargetSlot = (e, targetAttrName) => {
    const headerName = e.dataTransfer.getData(DRAG_MIME);

    // Trouver l'index de ce header
    const headerIndex = responseUpFile?.headers?.find(
      (h) => h.name === headerName
    )?.index;
    if (headerIndex === undefined) return;

    const newMap = produce(indexMappings || {}, (draft) => {
      // Retirer ce target des autres index
      Object.keys(draft).forEach((idx) => {
        if (includesTarget(draft[idx], targetAttrName)) {
          removeTargetForIndex(draft, idx, targetAttrName);
        }
      });
      // Ajouter au nouvel index
      addTargetForIndex(draft, headerIndex.toString(), targetAttrName);
    });
    setIndexMappings(newMap);

    // Override : target → index ✅
    const payload = {
      columnRemapping: { [targetAttrName]: headerIndex.toString() },
    };
    sendColumnRemapDebounced(payload);
  };

  const unmapTarget = (targetAttrName) => {
    const headerIndex = getIndexByTarget(targetAttrName);
    if (headerIndex) {
      const newMap = produce(indexMappings || {}, (draft) => {
        removeTargetForIndex(draft, headerIndex, targetAttrName);
      });
      setIndexMappings(newMap);
    }

    // Override : target → null ✅ (pareil)
    const payload = { columnRemapping: { [targetAttrName]: null } };
    sendColumnRemapDebounced(payload);
  };

  const unmapHeader = (headerName) => {
    // Trouver l'index de ce header
    const headerIndex = responseUpFile?.headers?.find(
      (h) => h.name === headerName
    )?.index;
    if (headerIndex === undefined) return;

    const indexStr = headerIndex.toString();
    const targets = asArray(indexMappings?.[indexStr]).filter(Boolean);

    const newMap = produce(indexMappings || {}, (draft) => {
      draft[indexStr] = null;
    });
    setIndexMappings(newMap);
    if (targets.length > 0) {
      const payload = {
        columnRemapping: Object.fromEntries(targets.map((t) => [t, null])),
      };
      sendColumnRemapDebounced(payload);
    }
  };

  // ---------- ENUM OVERRIDES

  const getEnumRule = useCallback(
    (targetAttr) => {
      const legacy = mappingRules?.fields?.[targetAttr]?.enum;
      if (legacy) {
        return {
          values: legacy.values ?? [],
          aliases: legacy.aliases ?? {},
          caseInsensitive:
            legacy.caseInsensitive ?? legacy.case_insensitive ?? true,
        };
      }
      const fa = fieldAnalysis?.[targetAttr]?.enum;
      if (fa) {
        return {
          values: fa.acceptedValues ?? [],
          aliases: {},
          caseInsensitive: true,
        };
      }
      return null;
    },
    [mappingRules, fieldAnalysis]
  );

  const getEnumTransformMap = useCallback(
    (targetAttr) => {
      const fa = fieldAnalysis?.[targetAttr]?.enum;
      const list = fa?.transformations ?? [];
      const norm = (s) => (s ?? '').toString().toLowerCase();
      const map = {};
      list.forEach((t) => {
        if (t?.original != null && t?.transformed != null) {
          map[norm(String(t.original).trim())] = String(t.transformed);
        }
      });
      return map;
    },
    [fieldAnalysis]
  );

  const setEnumOverride = (targetAttr, originalInput, toEnum) => {
    const key = (originalInput ?? '').toString().trim();
    const normKey = key.toLocaleLowerCase();
    setEnumOverrides((prev) => ({
      ...prev,
      [targetAttr]: {
        ...(prev[targetAttr] || {}),
        [normKey]: toEnum || null,
      },
    }));
  };

  const getEnumCandidates = (targetAttr) => {
    const fa = fieldAnalysis?.[targetAttr]?.enum;
    const arr = fa?.uniqueValuesFound ?? [];
    return arr.map((v) => toText(v));
  };

  const transformDisplayValue = (targetAttr, headerName, rawValue) => {
    const txt = toText(rawValue);
    if (!txt) return '';
    const rule = getEnumRule(targetAttr);
    const accepted = rule?.values || [];
    const ci = !!(rule?.caseInsensitive ?? rule?.case_insensitive);
    const norm = (s) => (ci ? s.toLocaleLowerCase() : s);
    const aliases = rule?.aliases || {};
    const overrides = enumOverrides?.[targetAttr] || {};
    const transMap = getEnumTransformMap(targetAttr);

    const key = norm(txt.trim());
    let mapped =
      overrides[key] ??
      aliases[key] ??
      transMap[key] ??
      (accepted.includes(txt) ? txt : txt);

    const ops = checked?.[headerName] || {};
    if (ops.uppercase) mapped = mapped.toUpperCase();
    if (ops.capitalize)
      mapped = mapped
        .split(/\s+/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
        .join(' ');

    return mapped;
  };

  // -------------------------------------------------
  // Render columns
  // -------------------------------------------------
  const colHeight = heightScreen / 1.8 - 50;

  // Column 1 — Source headers list
  const Column1 = (
    <ScrollArea style={{ height: colHeight }}>
      <Stack spacing="xs">
        <Group spacing="xs">
          <Text size="lg" weight={700}>
            Input fields
          </Text>
          <Text size="xs" color="dimmed">
            (Row {recordIndex + 1} on page, header row: {headerRowIndex})
          </Text>
        </Group>
        {visibleHeaders.map((h) => {
          const headerName = h.name;
          const value = toText(recordIn?.[headerName]);

          const headerIndex = responseUpFile?.headers?.find(
            (h) => h.name === headerName
          )?.index;
          const mappedTo =
            headerIndex !== undefined
              ? indexMappings?.[headerIndex.toString()]
              : null; // ✅ Par index
          const mappedList = asArray(mappedTo);

          return (
            <Group
              key={`src-${headerName}`}
              spacing="xs"
              position="apart"
              draggable
              onDragStart={(e) => onDragStartSource(e, headerName)}
              style={{
                border: `1px solid ${
                  mappedList.length
                    ? theme.colors.green[4]
                    : theme.colors.gray[4]
                }`,
                borderRadius: 6,
                padding: 8,
                background: '#fff',
              }}
              title="Drag me to a target row (column 2)"
            >
              <SafeCell label={headerName} value={value} />
              <Group spacing={6}>
                {mappedList.length > 0 && (
                  <Tooltip
                    withArrow
                    label={`Mapped → ${mappedList.join(', ')}`}
                  >
                    <Text size="xs" color="teal" style={{ fontWeight: 600 }}>
                      → {mappedList.join(', ')}
                    </Text>
                  </Tooltip>
                )}
                <ActionIcon
                  size="sm"
                  variant="light"
                  onClick={() => unmapHeader(headerName)}
                  title="Unmap all"
                >
                  <Trash size={14} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="light"
                  onClick={() => setColumnRemove((arr) => [...arr, headerName])}
                  title="Hide column"
                >
                  <X size={14} />
                </ActionIcon>
              </Group>
            </Group>
          );
        })}
      </Stack>
    </ScrollArea>
  );

  // Column 2 & 3 — Mapping grid + target preview
  const Column2and3 = (
    <ScrollArea style={{ height: colHeight }}>
      <Stack spacing="xs">
        <Group
          spacing="xl"
          noWrap
          style={{
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 1,
            paddingTop: 4,
            paddingBottom: 4,
          }}
        >
          <SheetSwitcher
            sheets={sheets}
            activeSheetId={String(activeSheet)}
            onChange={handleSheetChangeWithConfirm}
            compact
          />
          <Text size="lg" weight={700} style={{ width: 300 }}>
            Mapping slot{' '}
            <Text component="span" size="xs" color="dimmed">
              (input)
            </Text>
          </Text>
          <Text size="lg" weight={700} style={{ width: 300 }}>
            Object target{' '}
            <Text component="span" size="xs" color="dimmed">
              (output)
            </Text>
          </Text>
        </Group>

        {targetFields.map(({ value: targetAttr, label: targetLabel }) => {
          const mappedHeader = getHeaderByTarget(targetAttr);
          const rawVal = mappedHeader ? recordIn?.[mappedHeader] : '';

          const hasOverrideForCurrent = (() => {
            if (!mappedHeader) return false;
            const rule = getEnumRule(targetAttr);
            const ci = !!(rule?.caseInsensitive ?? rule?.case_insensitive);
            const norm = (s) => (ci ? s.toLowerCase() : s);
            const key = norm(toText(rawVal).trim());
            return !!enumOverrides?.[targetAttr]?.[key];
          })();

          const transformed = (() => {
            if (hasOverrideForCurrent) {
              return transformDisplayValue(targetAttr, mappedHeader, rawVal);
            }
            const fromServer = recordOut?.[targetAttr];
            if (fromServer != null && fromServer !== '')
              return toText(fromServer);
            if (mappedHeader)
              return transformDisplayValue(targetAttr, mappedHeader, rawVal);
            return '';
          })();

          const isSelected = selectedTarget === targetAttr;

          return (
            <Group
              key={`row-${targetAttr}`}
              align="stretch"
              spacing="xl"
              noWrap
              style={{
                background: 'var(--ds-bg-secondary)',
                padding: 10,
                borderRadius: 5,
              }}
            >
              {/* Column 2 — Drop slot */}
              <div
                onDragOver={allowDrop}
                onDrop={(e) => onDropToTargetSlot(e, targetAttr)}
                onClick={() => setSelectedTarget(targetAttr)}
                style={{
                  width: 300,
                  padding: 8,
                  border: `2px dashed ${
                    isSelected ? theme.colors.blue[5] : theme.colors.gray[4]
                  }`,
                  background: isSelected ? 'rgba(24, 119, 242, 0.06)' : '#fff',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
                title="Drop a source field here"
              >
                {mappedHeader ? (
                  <Group position="apart" spacing="xs">
                    <SafeCell label={mappedHeader} value={toText(rawVal)} />
                    <ActionIcon
                      size="sm"
                      variant="light"
                      onClick={(e) => {
                        e.stopPropagation();
                        unmapTarget(targetAttr);
                      }}
                      title="Remove mapping"
                    >
                      <Trash size={14} />
                    </ActionIcon>
                  </Group>
                ) : (
                  <Text size="xs" color="dimmed">
                    Drop a source field here…
                  </Text>
                )}
              </div>

              {/* Column 3 — Target descriptor with transformed preview */}
              <div
                onClick={() => setSelectedTarget(targetAttr)}
                style={{
                  width: 300,
                  padding: 8,
                  border: `1px solid ${
                    isSelected ? theme.colors.blue[5] : theme.colors.gray[4]
                  }`,
                  background: '#fff',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
                title="Select to configure transforms"
              >
                <SafeCell
                  label={`${targetLabel} (${targetAttr})`}
                  value={
                    mappedHeader
                      ? transformed
                      : toText(recordOut?.[targetAttr] ?? '')
                  }
                />
              </div>
            </Group>
          );
        })}
      </Stack>
    </ScrollArea>
  );

  // Column 4 — Transform config (includes ENUM grid)
  const Column4 = (
    <ScrollArea style={{ height: colHeight }}>
      <Stack spacing="xs">
        <Text size="lg" weight={700}>
          Transform
        </Text>

        {!selectedTarget && (
          <Text size="xs" color="dimmed">
            Select a target row (col 3) to configure its mapping.
          </Text>
        )}

        {selectedTarget &&
          (() => {
            const targetAttr = selectedTarget;
            const headerName = selectedHeaderForConfig;
            const enumRule = getEnumRule(targetAttr);
            const cfg = headerName
              ? checked?.[headerName] ?? { uppercase: false, capitalize: false }
              : null;

            return (
              <Stack
                spacing="xs"
                style={{
                  border: `1px solid ${theme.colors.gray[4]}`,
                  borderRadius: 6,
                  padding: 8,
                }}
              >
                <Text size="sm" weight={600}>
                  Mapping: <code>{headerName || '—'}</code> →{' '}
                  <code>{targetAttr}</code>
                </Text>

                {headerName && (
                  <>
                    <Checkbox
                      label="Uppercase"
                      checked={!!cfg?.uppercase}
                      onChange={(e) =>
                        setChecked({
                          ...checked,
                          [headerName]: {
                            ...(cfg || {}),
                            uppercase: e.currentTarget.checked,
                          },
                        })
                      }
                    />
                    <Checkbox
                      label="Capitalize"
                      checked={!!cfg?.capitalize}
                      onChange={(e) =>
                        setChecked({
                          ...checked,
                          [headerName]: {
                            ...(cfg || {}),
                            capitalize: e.currentTarget.checked,
                          },
                        })
                      }
                    />
                  </>
                )}

                {enumRule && (
                  <>
                    <Space h={4} />
                    <Text size="sm" weight={600}>
                      Enumeration mapping
                    </Text>
                    <Text size="xs" color="dimmed">
                      Define how input values map to accepted values: [
                      {(enumRule.values || []).join(', ')}]
                    </Text>

                    <Table fontSize="xs" verticalSpacing="xs" highlightOnHover>
                      <thead>
                        <tr>
                          <th style={{ width: 220 }}>Input value</th>
                          <th style={{ width: 220 }}>Output enum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const candidates = getEnumCandidates(targetAttr);
                          const accepted = enumRule.values || [];
                          const ci = !!enumRule.caseInsensitive;
                          const norm = (s) =>
                            ci
                              ? (s ?? '').toString().toLocaleLowerCase()
                              : (s ?? '').toString();
                          const aliases = enumRule.aliases || {};
                          const overrides = enumOverrides?.[targetAttr] || {};
                          const transMap = getEnumTransformMap(targetAttr);

                          return candidates.map((orig) => {
                            const nkey = norm((orig ?? '').toString().trim());
                            const current =
                              overrides[nkey] ??
                              aliases[nkey] ??
                              transMap[nkey] ??
                              (accepted.includes(orig) ? orig : '');

                            return (
                              <tr key={`${targetAttr}-${nkey}`}>
                                <td>
                                  <Text size="xs">{orig}</Text>
                                </td>
                                <td>
                                  <Select
                                    size="xs"
                                    searchable
                                    clearable
                                    placeholder="Select enum…"
                                    data={accepted.map((v) => ({
                                      value: v,
                                      label: v,
                                    }))}
                                    value={current || null}
                                    onChange={(val) => {
                                      setEnumOverride(targetAttr, orig, val);
                                      const payload = {
                                        enumCorrections: {
                                          [targetAttr]: [
                                            {
                                              originalValue: orig ?? '',
                                              correctedValue: val ?? '',
                                              addToAliases: true,
                                            },
                                          ],
                                        },
                                      };
                                      sendEnumOverrideDebounced(payload);
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </Table>
                  </>
                )}
              </Stack>
            );
          })()}

        <Space h={8} />
        <Group position="right">
          <Button variant="default" onClick={prevStep} disabled={isLoading}>
            Prev step
          </Button>
          <Button onClick={handleNextStepMappingData} loading={isLoading}>
            Next step
          </Button>
        </Group>
      </Stack>
    </ScrollArea>
  );

  // -------------------------------------------------
  // Whole layout
  // -------------------------------------------------
  return (
    <div
      style={{
        height: heightScreen / 1.8,
        maxHeight: heightScreen / 1.8,
        position: 'relative',
      }}
    >
      <LoadingOverlay
        style={{ borderRadius: 4 }}
        visible={isLoadingLoadDataExcel}
        overlayOpacity={0.3}
        overlayColor="#c5c5c5"
      />

      <Group align="flex-start" spacing="md" noWrap>
        {/* Column 1 */}
        <Box style={{ width: 320 }}>{Column1}</Box>

        {/* Columns 2 & 3 */}
        <Box style={{ width: 640 }}>{Column2and3}</Box>

        {/* Column 4 */}
        <Box style={{ flex: 1, minWidth: 300 }}>{Column4}</Box>
      </Group>

      {/* Bottom navigator */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: -8 }}>
        <Group position="apart" noWrap>
          <Group spacing="xs">
            <Button
              size="xs"
              variant="default"
              onClick={prevRecord}
              disabled={recordIndex <= 0}
            >
              Prev row
            </Button>
            <Text size="xs">
              Row {Math.min(recordIndex + 1, Math.max(pageData.length, 0))} /{' '}
              {Math.max(pageData.length, 0)}
            </Text>
            <Button
              size="xs"
              variant="default"
              onClick={nextRecord}
              disabled={recordIndex >= pageData.length - 1}
            >
              Next row
            </Button>
          </Group>

          {responseUpFile && (
            <Pagination
              size="sm"
              total={responseUpFile?.pagesTotal ?? 1}
              page={activePage}
              onChange={(page) => {
                getDataFromExcel(page);
                setPage(page);
              }}
            />
          )}
        </Group>
      </div>
    </div>
  );
}
